import { CaretDown } from "@phosphor-icons/react";
import { forwardRef, useId, useState, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import { twRing, twTransform } from "@/lib/emotionTw";

export type AccordionItem = {
  value: string;
  title: ReactNode;
  description?: ReactNode;
  content: ReactNode;
  disabled?: boolean;
};

export type AccordionType = "single" | "multiple";

export type AccordionProps = Omit<HTMLAttributes<HTMLDivElement>, "defaultValue" | "onChange"> & {
  items: AccordionItem[];
  /** `"single"` keeps one panel open at a time; `"multiple"` allows several. */
  type?: AccordionType;
  /** Controlled open value(s): a string for `single`, a string array for `multiple`. */
  value?: string | string[];
  /** Uncontrolled initial open value(s). */
  defaultValue?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  /** When `type="single"`, allow closing the open panel by clicking it again. */
  collapsible?: boolean;
};

/* Exact compiled output of the pre-migration Tailwind utilities. */

const AccordionRoot = styled("div", {
  label: "AgenticAccordion-root"
})({
  overflow: "hidden",
  borderRadius: "var(--bfrs-radius-xl)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
});

const AccordionItemWrap = styled("div", {
  label: "AgenticAccordion-item",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { divided: boolean } }>(({ ownerState }) => [
  ownerState.divided && {
    borderTopWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))"
  }
]);

const AccordionTrigger = styled("button", {
  label: "AgenticAccordion-trigger"
})({
  display: "flex",
  width: "100%",
  alignItems: "center",
  gap: ".75rem",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem",
  textAlign: "left",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)"),
  "&:disabled": {
    cursor: "not-allowed",
    opacity: ".6"
  }
});

const AccordionHeaderText = styled("span", {
  label: "AgenticAccordion-headerText"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const AccordionTitle = styled("span", {
  label: "AgenticAccordion-title"
})({
  display: "block",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const AccordionDescription = styled("span", {
  label: "AgenticAccordion-description"
})({
  marginTop: ".125rem",
  display: "block",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const AccordionCaret = styled(CaretDown, {
  label: "AgenticAccordion-caret",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { open: boolean } }>(({ ownerState }) => [
  {
    flexShrink: 0,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.open && twTransform({ "--tw-rotate": "180deg" })
]);

const AccordionPanel = styled("div", {
  label: "AgenticAccordion-panel"
})({
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem"
});

const toArray = (value: string | string[] | undefined): string[] => {
  if (value === undefined || value === "") return [];
  return Array.isArray(value) ? value : [value];
};

export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  ({ className, items, type = "single", value, defaultValue, onValueChange, collapsible = true, ...props }, ref) => {
    const baseId = useId();
    const isControlled = value !== undefined;
    const [internalOpen, setInternalOpen] = useState<string[]>(() => toArray(defaultValue));
    const openValues = isControlled ? toArray(value) : internalOpen;

    const commit = (next: string[]) => {
      if (!isControlled) setInternalOpen(next);
      onValueChange?.(type === "single" ? next[0] ?? "" : next);
    };

    const toggle = (itemValue: string) => {
      const isOpen = openValues.includes(itemValue);

      if (type === "single") {
        if (isOpen) {
          commit(collapsible ? [] : openValues);
        } else {
          commit([itemValue]);
        }
        return;
      }

      commit(isOpen ? openValues.filter((current) => current !== itemValue) : [...openValues, itemValue]);
    };

    return (
      <AccordionRoot ref={ref} className={className} {...props}>
        {items.map((item, index) => {
          const isOpen = openValues.includes(item.value);
          const triggerId = `${baseId}-trigger-${index}`;
          const panelId = `${baseId}-panel-${index}`;

          return (
            <AccordionItemWrap key={item.value} ownerState={{ divided: index > 0 }}>
              <AccordionTrigger
                type="button"
                id={triggerId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                disabled={item.disabled}
                onClick={() => toggle(item.value)}
              >
                <AccordionHeaderText>
                  <AccordionTitle>{item.title}</AccordionTitle>
                  {item.description && <AccordionDescription>{item.description}</AccordionDescription>}
                </AccordionHeaderText>
                <AccordionCaret size={16} weight="bold" ownerState={{ open: isOpen }} />
              </AccordionTrigger>
              {isOpen && (
                <AccordionPanel id={panelId} role="region" aria-labelledby={triggerId}>
                  {item.content}
                </AccordionPanel>
              )}
            </AccordionItemWrap>
          );
        })}
      </AccordionRoot>
    );
  }
);

Accordion.displayName = "Accordion";
